import { motion } from "framer-motion";
import { Calendar, MessageCircle, Stethoscope, Award, Building2, Scissors } from "lucide-react";

const trustBadges = [
  { icon: Award, label: "10+ Years Experience" },
  { icon: Scissors, label: "500+ Cancer Surgeries" },
  { icon: Building2, label: "Associated with Leading Hospitals in Kolkata" },
  { icon: Stethoscope, label: "Specialized in HIPEC & Advanced Laparoscopic Surgery" },
];

export default function HeroSection() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 md:pt-40 overflow-hidden bg-[#F9F9F6]"
    >
      <div className="max-w-7xl mx-auto px-6 py-6 md:px-12 lg:px-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center py-12 md:py-0">
          {/* Left: Content */}
          <div className="order-2 lg:order-1">
            {/* <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-sm sm:text-base font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4"
              data-testid="hero-subtitle-label"
            >
              Gynecologic Oncologist, Kolkata
            </motion.p> */}

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ lineHeight: "1.2" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="font-serif  md:text-[2.5rem] text-[1.8rem] tracking-tighter leading-[1.2] text-[#1E3F33] mb-6"
              data-testid="hero-headline"
            >
              Best Gynecologic Oncologist
              <br />
              in Kolkata  for Ovarian, Cervical
              <br />
              <span className="text-[#8FA998]">& Uterine Cancer Treatment</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-lg mb-8"
              data-testid="hero-subheadline"
            >
              Advanced, compassionate cancer care for women by Dr. Neha Agarwal – Specialist in gynecologic cancer surgery, HIPEC, and minimally invasive oncology treatments in Kolkata.            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="flex flex-wrap gap-3 mb-10"
            >
              <a
                href="/book-appointment"
                data-testid="hero-book-btn"
                className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors duration-200"
              >
                <Calendar className="w-4 h-4" />
                Book Appointment
              </a>
              <a
                href="https://wa.me/916291000829"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 border-2 border-[#1E3F33] text-[#1E3F33] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#1E3F33] hover:text-[#F9F9F6] transition-all duration-200"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
              {/* <a
                href="#about"
                data-testid="hero-second-opinion-btn"
                className="inline-flex items-center gap-2 text-[#4A6359] px-5 py-3.5 text-sm font-semibold hover:text-[#1E3F33] transition-colors duration-200 underline underline-offset-4 decoration-[#8FA998]"
              >
                Get Second Opinion
              </a> */}
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="grid grid-cols-2 gap-4"
              data-testid="hero-trust-badges"
            >
              {trustBadges.map((badge, i) => (
                <div
                  key={badge.label}
                  data-testid={`trust-badge-${i}`}
                  className="flex items-center gap-3 bg-[#F2EFE9] px-4 py-3 rounded-2xl"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1E3F33]/10 flex items-center justify-center">
                    <badge.icon className="w-5 h-5 text-[#1E3F33]" />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-[#1E3F33] leading-tight">
                    {badge.label}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Doctor Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="order-1 lg:order-2 flex justify-center lg:justify-end"
            data-testid="hero-image-container"
          >
            <div className="relative">
              <div className="w-[280px] h-[360px] sm:w-[340px] sm:h-[440px] lg:w-[420px] lg:h-[540px] rounded-[40px] md:rounded-[80px] rounded-br-[20px] overflow-hidden bg-[#E6E7E2]">
                <img
                  src="images/about.png"
                  alt="Dr. Neha Agarwal - Gynecologic Oncologist"
                  data-testid="hero-doctor-image"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 sm:-bottom-5 sm:-left-6 bg-[#F9F9F6] shadow-xl shadow-[#1E3F33]/10 rounded-2xl px-5 py-3 border border-[#D8DDD9]">
                <p className="text-xs text-[#4A6359] font-medium">Specialist in</p>
                <p className="text-sm font-bold text-[#1E3F33]">Gynecologic Oncology</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
